import type {
    NavigationItemData,
    PartnerData,
    SocialLinkData,
    VenueId,
} from '@beat-venues/common-types';
import type { Document } from '@contentful/rich-text-types';
import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import {
    Container,
    RichText,
    TextLink,
    Flocks,
    PartnerList,
    SocialList,
} from '../../';
import type { CommonProps } from '../../types';

export interface FooterProps
    extends CommonProps,
        VariantProps<typeof FooterStyles> {
    links: NavigationItemData[];
    legalLinks: NavigationItemData[];
    socialLinks: SocialLinkData[];
    partners: PartnerData[];
    footerCopy: Document;
    venueId: VenueId;
}

export const FooterStyles = cva('border-t-brand-500 border-t-2');

export const Footer = ({
    className,
    links,
    legalLinks,
    socialLinks,
    partners,
    footerCopy,
    venueId,
    testId = 'footer',
    ...props
}: FooterProps): JSX.Element => {
    return (
        <footer
            role="contentinfo"
            className={twMerge(FooterStyles({ className }))}
            {...props}
            data-testid={testId}
        >
            <Flocks composition="footer" venue={venueId}>
                <Container className="py-16" maxWidth="lg">
                    <div className="xs:mb-16 xs:flex-row flex flex-col justify-between gap-14 md:mb-24">
                        <ul className="xs:items-start flex flex-col items-center gap-5">
                            {links?.map((link) => (
                                <li key={link.id}>
                                    <TextLink
                                        className="text-2xl"
                                        href={link.href}
                                        intent="secondary"
                                        underline={false}
                                    >
                                        {link.label}
                                    </TextLink>
                                </li>
                            ))}
                        </ul>

                        <div className="xs:text-right flex flex-col justify-start gap-10 text-center">
                            {socialLinks && (
                                <SocialList socialLinks={socialLinks} />
                            )}

                            {partners && (
                                <PartnerList
                                    className="xs:hidden"
                                    partners={partners}
                                />
                            )}

                            <RichText body={footerCopy} />
                        </div>
                    </div>
                    {partners && (
                        <PartnerList
                            className="xs:mb-16 xs:flex hidden md:mb-20"
                            partners={partners}
                        />
                    )}

                    <ul className="mt-8 flex flex-wrap justify-center gap-3 text-center md:gap-x-5 lg:gap-x-8">
                        {legalLinks?.map((legalLink) => {
                            return (
                                <li
                                    key={legalLink.id}
                                    aria-label={`${legalLink.label}, link`}
                                >
                                    <TextLink
                                        href={legalLink.href}
                                        size="sm"
                                        underline={false}
                                    >
                                        {legalLink.label}
                                    </TextLink>
                                </li>
                            );
                        })}
                    </ul>
                </Container>
            </Flocks>
        </footer>
    );
};
